import { useState, useRef, useEffect, useCallback } from "react";
import { UseSelectOptions, UseSelectReturn } from "./Select.types";
import { useSelectKeyboard, useSelectSearch, useSelectMulti } from "./hooks";

export const useSelect = ({
  options,
  value,
  variant = "single",
  disabled = false,
  loading = false,
  searchable = false,
  maxSelections,
  showAllOption = false,
  onSearch,
  searchDebounceMs = 300,
  minSearchLength = 0,
  onChange,
  onAllToggle,
}: UseSelectOptions): UseSelectReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const isMulti = variant === "multi";

  // Use specialized hooks for different functionalities
  const {
    searchTerm,
    filteredOptions,
    handleSearchChange,
    setSearchTerm,
  } = useSelectSearch({
    options,
    searchable,
    onSearch,
    searchDebounceMs,
    minSearchLength,
  });

  const {
    selectedValues,
    selectedOptions,
    allSelected,
    handleOptionClick,
    handleSelectAll,
    handleDeselectAll,
    handleAllToggle,
    handleRemoveTag,
    handleClear,
  } = useSelectMulti({
    options,
    value,
    isMulti,
    showAllOption,
    maxSelections,
    onChange,
    onAllToggle,
  });

  // Derived state
  const hasValue = selectedValues.length > 0 && !allSelected;
  const hasAnySelection = selectedValues.length > 0;

  // Toggle dropdown open/close
  const handleToggle = useCallback(() => {
    if (!disabled && !loading && !allSelected) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);

      if (newIsOpen && searchable) {
        // Focus search input when opening
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }
  }, [disabled, loading, isOpen, searchable, allSelected]);

  // Close dropdown
  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Do not clear search on close, as per user request to persist search state
  }, []);

  // Handle option click with single select auto-close
  const handleOptionClickWithClose = useCallback((optionValue: string) => {
    handleOptionClick(optionValue);

    // Close dropdown for single select
    if (!isMulti) {
      setIsOpen(false);
    }
  }, [handleOptionClick, isMulti]);

  // Keyboard navigation
  const { handleKeyDown } = useSelectKeyboard({
    isOpen,
    searchable,
    onToggle: handleToggle,
    onClose: handleClose,
  });

  // Close dropdown when all options are selected
  useEffect(() => {
    if (allSelected && isOpen) {
      setIsOpen(false);
    }
  }, [allSelected, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, handleClose]);

  return {
    // State
    isOpen,
    searchTerm,
    selectedValues,
    selectedOptions,
    filteredOptions,
    allSelected,
    hasValue,
    hasAnySelection,
    isMulti,

    // Actions
    handleToggle,
    handleOptionClick: handleOptionClickWithClose,
    handleClear,
    handleSelectAll,
    handleDeselectAll,
    handleAllToggle,
    handleRemoveTag,
    handleKeyDown,
    handleSearchChange,
    setIsOpen,
    setSearchTerm,

    // Refs
    selectRef,
    searchInputRef,
  };
};
